import React, { useEffect, useMemo, useRef } from "react";
import { Loading } from "@src/common/components";
import {
  Driver,
  DispatchScope,
  Maybe,
  OrderingAlgorithmType,
  Shipment,
  Stop,
} from "@api/graphql/generated/generated-types";
import { ReactSortable } from "react-sortablejs";
import classNames from "classnames";
import {
  DriverRouteDataProvider,
  useDriverRouteDataProvider,
} from "./contexts/DriverRouteDataProviderContext";
import { TaskStopCardComponent } from "./components/TaskStopCardComponent";
import RouteSelectionHeader from "./components/RouteSelectionHeader";
import MapOnDriverRouteView from "./components/MapOnDriverRouteView";
import {
  DriverRouteViewSelectionProvider,
  useDriverRouteViewSelectionProvider,
} from "./contexts/DriverRouteViewSelectionProvider";
import {
  getTabOrder,
  orderingAlgorithmDisplayNames,
} from "./utils/tabConstants";
import { formatTitleCaseWithUnderscores } from "@src/common/lib/textUtils";
import RouteSettingsPanel from "./components/RouteSettingsPanel";
import { useAuth } from "@src/common/hooks/useAuth";
import { useViewportHeight } from "./hooks/useViewportHeight";
import { useDriverBaseTime } from "./hooks/useDriverBaseTime";
import { useRouteEtaUpdates } from "./hooks/useRouteEtaUpdates";
import { useRouteOptimization } from "./hooks/useRouteOptimization";
import { useRouteAlgorithmSelection } from "./hooks/useRouteAlgorithmSelection";
import { useStopSelection } from "./hooks/useStopSelection";
import { getHighlightedShipmentIds } from "./utils/highlightedShipmentIds";

export enum RouteViewMode {
  Dispatch = "Dispatch",
  ChangeDriver = "ChangeDriver",
  ChangeRoute = "ChangeRoute",
}

type Props = {
  loading: boolean;
  routeDetails: Stop[] | undefined;
  setRouteDetails: (value: Stop[] | undefined) => void;
  setIsValidOrder: (value: boolean) => void;
  isValidOrder: boolean;
  selectedShipment?: Shipment | undefined;
  driver?: Maybe<Driver> | undefined;
  // Controlled component pattern: parent maintains authoritative algorithm state
  // Used for: 1) backend API calls, 2) manual override, 3) multi-day persistence
  onAlgorithmTypeChange?: (value: OrderingAlgorithmType) => void;
  algorithmType?: OrderingAlgorithmType;
  routeViewMode?: RouteViewMode;
  setAutoRoutedStopIds?: (value: undefined | string[]) => void;
  setErrorMessage?: React.Dispatch<React.SetStateAction<string | null>>;
  selectedStopDate?: string;
  onClickManual?: any;
  setLoading?: (value: boolean) => void;
  // Turns the sequence bubbles into checkboxes on hover so a subset of the
  // route can be acted on. Off by default.
  isSelectionEnabled?: boolean;
  // Called with the checked stops when the header's transfer button is used.
  onTransferSelection?: (selectedStopIds: string[]) => void;
  onSelectionChange?: (selectedStopIds: string[]) => void;
  // ASSIGNMENT_GROUP only from the dispatch modal; everyone else leaves it unset.
  dispatchScope?: DispatchScope;
};

function ChangeDriverRouteView({
  loading,
  routeDetails,
  setRouteDetails,
  setIsValidOrder,
  isValidOrder,
  selectedShipment,
  driver,
  // Rename for clarity: these props come from parent component
  onAlgorithmTypeChange,
  algorithmType: parentAlgorithmType,
  routeViewMode = RouteViewMode.ChangeRoute,
  setAutoRoutedStopIds,
  setErrorMessage,
  selectedStopDate,
  onClickManual,
  setLoading,
  isSelectionEnabled = false,
  onTransferSelection,
  onSelectionChange,
  dispatchScope,
}: Props) {
  const { courierId } = useAuth();
  const {
    PreviewEtaForRouteMutationData,
    OptimizeRouteForDispatchMutationData,
    OptimizeRouteForDispatchMutationLoading,
  } = useDriverRouteDataProvider();
  // Local component state managed by context - this is the working copy
  const {
    setSelectedOrderingAlgorithmType,
    selectedOrderingAlgorithmType,
    selectedBaseTime,
  } = useDriverRouteViewSelectionProvider();

  const mapContainerRef = useRef<HTMLDivElement>(null);
  // shipmentId groups an order's stops so they select together; stopDate lets
  // the selection survive day switches on a multi-day route.
  const selectableStops = useMemo(
    () =>
      routeDetails?.map((stop) => ({
        id: stop.id,
        shipmentId: stop.shipmentId || stop.shipment?.id,
        stopDate: stop.effectiveStopDate || stop.stopDate,
      })) || [],
    [routeDetails]
  );
  const selection = useStopSelection(selectableStops);
  const isSelectionMode = isSelectionEnabled && selection.isSelectionMode;
  const OrderingAlgorithmTypes = Object.values(OrderingAlgorithmType);
  const tabOrder = getTabOrder(routeViewMode);

  useDriverBaseTime(selectedStopDate, driver, courierId);
  const viewportHeight = useViewportHeight(mapContainerRef, routeViewMode);
  const { updateEtaForRouteDetails, checkRouteOrder } = useRouteEtaUpdates({
    driver,
    selectedStopDate,
    setIsValidOrder,
    setErrorMessage,
  });
  const { handleOrderingAlgorithmChange } = useRouteOptimization({
    driver,
    selectedShipment,
    selectedStopDate,
    courierId,
    tabOrder,
    OrderingAlgorithmTypes,
    setLoading,
    setErrorMessage,
    setIsValidOrder,
    onAlgorithmTypeChange,
    dispatchScope,
  });

  // Sync parent's algorithm selection to local state on mount/change
  // This maintains the parent as the source of truth while allowing local mutations
  useEffect(() => {
    if (parentAlgorithmType) {
      setSelectedOrderingAlgorithmType(parentAlgorithmType);
    }
  }, [parentAlgorithmType]);

  // Handle route algorithm selection based on view mode
  // This hook orchestrates different algorithm behaviors for Dispatch/ChangeRoute/ChangeDriver modes
  useRouteAlgorithmSelection({
    routeViewMode,
    selectedOrderingAlgorithmType,
    parentAlgorithmType,
    tabOrder,
    handleOrderingAlgorithmChange,
    onClickManual,
    setSelectedOrderingAlgorithmType,
    selectedStopDate,
    selectedShipment,
    selectedBaseTime,
    driver,
  });

  useEffect(() => {
    onSelectionChange && onSelectionChange(selection.selectedStopIds);
  }, [selection.selectedStopIds]);

  useEffect(() => {
    if (!routeDetails || routeDetails.length === 0) {
      return;
    }

    checkRouteOrder(routeDetails);
    updateEtaForRouteDetails(routeDetails);
  }, [routeDetails, driver]);

  useEffect(() => {
    if (!OptimizeRouteForDispatchMutationData) {
      return;
    }
    setLoading && setLoading(false);
    const optimizedStops = OptimizeRouteForDispatchMutationData
      ?.optimizeRouteForDispatch.stops as Stop[];
    const optimizedStopIds = optimizedStops?.map((stop) => stop.id);
    setAutoRoutedStopIds && setAutoRoutedStopIds(optimizedStopIds);
    if (optimizedStops) {
      checkRouteOrder(optimizedStops);
      setRouteDetails(optimizedStops);
      updateEtaForRouteDetails(optimizedStops);
    }
  }, [OptimizeRouteForDispatchMutationData]);

  const highlightedShipmentIds = getHighlightedShipmentIds(
    selectedShipment?.id,
    OptimizeRouteForDispatchMutationData?.optimizeRouteForDispatch
      .allShipmentIdsForGroupDispatch
  );

  return (
    <Loading loading={loading} className="h-full grid grid-rows-[auto,1fr]">
      <div className="flex gap-2 row-span-2">
        <Loading
          loading={OptimizeRouteForDispatchMutationLoading}
          text="Optimizing route"
          className="w-1/3"
        >
          {routeDetails && (
            <div className="w-full h-full flex flex-col min-h-0">
              {isSelectionEnabled && (
                <RouteSelectionHeader
                  selectedCount={selection.selectedCount}
                  visibleSelectedCount={selection.visibleSelectedCount}
                  visibleTotalCount={selection.visibleTotalCount}
                  onTransfer={
                    onTransferSelection &&
                    (() => onTransferSelection(selection.selectedStopIds))
                  }
                />
              )}
              <ReactSortable
                animation={300}
                ghostClass="ghost"
                list={routeDetails.map((x) => ({ ...x, chosen: true }))}
                setList={setRouteDetails}
                scroll
                bubbleScroll={false}
                scrollSensitivity={100}
                scrollSpeed={10}
                // Reordering is turned off while stops are selected so a
                // click on a card can only mean "check this one".
                disabled={isSelectionMode}
                className={classNames({
                  "sortable-list border border-b-8 border-t-8 bg-gray-100 dark:bg-gray-800 p-2 row-span-2 overflow-auto rounded-md w-full flex-1 min-h-0":
                    true,
                  "border-red-400 dark:border-red-500": !isValidOrder,
                  "border-slate-200 dark:border-gray-700": isValidOrder,
                })}
              >
                {routeDetails.map((task, index) => {
                  const stop = task as Stop;
                  const eta =
                    PreviewEtaForRouteMutationData?.previewEtaForRoute?.stopEtas?.find(
                      (stopEta) => stopEta.stopId === stop.id
                    );
                  return (
                    <TaskStopCardComponent
                      key={index}
                      task={task}
                      index={index}
                      stop={stop}
                      highlightedShipmentIds={highlightedShipmentIds}
                      eta={eta}
                      selectedStopDate={selectedStopDate}
                      selection={{
                        isSelectionEnabled,
                        isSelectionMode,
                        isSelected: selection.isSelected(stop.id),
                        isAutoSelected: selection.isAutoSelected(stop.id),
                        onToggle: (extendRange: boolean) =>
                          selection.toggleStopAt(index, extendRange),
                      }}
                    />
                  );
                })}
              </ReactSortable>
            </div>
          )}
        </Loading>
        <div className="w-2/3 flex relative" ref={mapContainerRef}>
          {selectedShipment && ( // Only show tabs if there is a selected shipment to optimize for
            <div className="absolute z-10 w-full flex justify-center backdrop-blur-lg px-20 py-0.5">
              {tabOrder.map((algorithmType, key) => {
                const displayName =
                  orderingAlgorithmDisplayNames[algorithmType] ||
                  formatTitleCaseWithUnderscores(algorithmType);

                return (
                  <span
                    className={`flex-1 text-sm font-medium leading-5 py-2 text-center rounded-lg ${
                      OptimizeRouteForDispatchMutationLoading
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    } ${
                      selectedOrderingAlgorithmType === algorithmType
                        ? "bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-400 shadow ring-2 ring-white dark:ring-gray-700 ring-offset-2 ring-offset-blue-400 dark:ring-offset-blue-500"
                        : "text-slate-800 dark:text-gray-200 hover:bg-white/[0.12] dark:hover:bg-gray-700/50 hover:text-primary-600 dark:hover:text-primary-400"
                    }`}
                    key={key}
                    onClick={() => {
                      if (OptimizeRouteForDispatchMutationLoading) {
                        return;
                      }
                      const index = tabOrder.findIndex(
                        (type) => type === algorithmType
                      );
                      handleOrderingAlgorithmChange(index);
                    }}
                  >
                    {displayName}
                  </span>
                );
              })}
            </div>
          )}

          <RouteSettingsPanel
            handleOrderingAlgorithmChange={handleOrderingAlgorithmChange}
            updateEtaForRouteDetails={updateEtaForRouteDetails}
            routeDetails={routeDetails}
            tabOrder={tabOrder}
          />

          <MapOnDriverRouteView
            routeDetails={routeDetails}
            viewportHeight={`${viewportHeight}`}
            driver={driver}
          />
        </div>
      </div>
    </Loading>
  );
}

function withDriverRouteDataProviderContext(Component: any) {
  return function WrappedComponent(props: Props) {
    if (!props) {
      return null;
    }
    return (
      <DriverRouteDataProvider>
        <DriverRouteViewSelectionProvider
          initialAlgorithmType={props.algorithmType}
        >
          <Component {...props} />
        </DriverRouteViewSelectionProvider>
      </DriverRouteDataProvider>
    );
  };
}

export default withDriverRouteDataProviderContext(ChangeDriverRouteView);
